import { createSlice } from "@reduxjs/toolkit";
import update from "react-addons-update";
import axios from "utils/axios";
import { openSnackbarWithError } from "utils/errors";
import { dispatch } from "../index";
import { decrementSuperAthleteCount } from "./dashboard";
import { openSnackbar } from "./snackbar";

const initialState = {
  data: [],
  params: null,
  total: 0,
  page: 0,
  limit: 100,
  isLoading: false,
  isError: false,
  // === Popup ===
  isAddingAthlete: false,
  isLoadingAthlete: false,
  isPosting: false,
  isPostingError: false,
  openedAthlete: null,
  isDeleting: false,
};

const slice = createSlice({
  name: "superathletes",
  initialState,
  reducers: {
    getSuperAthletes(state, action) {
      state.isLoading = true;
    },
    getSuperAthletesSuccess(state, action) {
      state.isLoading = false;
      state.isError = false;
      state.data = action.payload.data;
      state.total = action.payload.total_count;
      state.page = action.payload.request.offset;
      state.limit = action.payload.request.limit;
    },
    getSuperAthletesFailure(state, action) {
      state.superAthletes = [];
      state.isLoading = false;
      state.isError = true;
    },

    setPaginationParams(state, action) {
      state.params = action.payload;
    },

    // =====================================
    postSuperAthlete(state, action) {
      state.isPosting = true;
      state.isPostingError = false;
    },

    postSuperAthleteSuccess(state, action) {
      state.isPosting = false;
      state.isPostingError = false;
      state.data.push(action.payload);
      if (!action.payload.isAddingMore) {
        state.isAddingAthlete = false;
      }
    },

    postSuperAthleteFailure(state, action) {
      state.isPosting = false;
      state.isPostingError = true;
      state.error = action.payload;
    },

    // =====================================
    getSuperAthlete(state, action) {
      state.isLoadingAthlete = true;
    },

    getSuperAthleteSuccess(state, action) {
      state.openedAthlete = action.payload;
      state.isLoadingAthlete = false;
    },

    getSuperAthleteFailure(state, action) {
      state.isLoadingAthlete = false;
      state.error = action.payload;
    },

    closeSuperAthlete(state, action) {
      state.isLoadingAthlete = false;
      state.openedAthlete = null;
    },

    isAddingAthlete(state, action) {
      state.isAddingAthlete = action.payload;
    },

    // =====================================
    putSuperAthlete(state, action) {
      state.isPosting = true;
      state.isPostingError = false;
    },

    putSuperAthleteSuccess(state, action) {
      state.isPosting = false;
      state.isPostingError = false;
      const index = state.data.findIndex((sa) => sa.id === action.payload.id);
      state.openedAthlete = null;
      state.data = update(state.data, {
        [index]: {
          $set: action.payload,
        },
      });
    },

    putSuperAthleteFailure(state, action) {
      state.isPosting = false;
      state.isPostingError = true;
      state.error = action.payload;
    },

    // =====================================
    deleteSuperAthlete(state, action) {
      state.isDeleting = true;
      state.isDeletingError = false;
    },

    deleteSuperAthleteSuccess(state, action) {
      state.isDeleting = false;
      state.isDeletingError = false;
      const index = state.data.findIndex((sa) => sa.id === action.payload);
      state.data = update(state.data, { $splice: [[index, 1]] });
      state.openedAthlete = null;
    },

    deleteSuperAthleteFailure(state, action) {
      state.isDeleting = false;
      state.isDeletingError = true;
      state.error = action.payload;
    },
  },
});

// Reducer
export default slice.reducer;

// ----------------------------------------------------------------------

export function getSuperAthletes(lastName, offset = 0, limit = 100) {
  return async (dispatch) => {
    dispatch(slice.actions.getSuperAthletes());
    try {
      dispatch(
        slice.actions.setPaginationParams({
          lastName: lastName !== "" ? lastName : null,

          offset,
          limit,
        })
      );
      const response = await axios.get("/api/v1/super_athletes", {
        params: {
          lastName: lastName !== "" ? lastName : null,
          offset,
          limit,
        },
      });
      dispatch(slice.actions.getSuperAthletesSuccess(response.data));
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.getSuperAthletesFailure(error));
    }
  };
}

export function getSuperAthlete(id) {
  return async (dispatch) => {
    dispatch(slice.actions.getSuperAthlete());
    try {
      const response = await axios.get(`/api/v1/super_athletes/${id}`);
      dispatch(slice.actions.getSuperAthleteSuccess(response.data));
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.getSuperAthleteFailure(error));
    }
  };
}

export function postSuperAthlete(data, isSaveWithAddingMore = false) {
  dispatch(slice.actions.postSuperAthlete());
  return async (dispatch) => {
    try {
      const response = await axios.post("/api/v1/super_athletes", data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      dispatch(
        slice.actions.postSuperAthleteSuccess({
          ...response.data,
          isAddingMore: isSaveWithAddingMore,
        })
      );
    } catch (error) {
      console.log(error);
      openSnackbarWithError(error, dispatch);
      dispatch(slice.actions.postSuperAthleteFailure(error));
    }
  };
}

export function putSuperAthlete(data) {
  dispatch(slice.actions.putSuperAthlete());
  return async (dispatch) => {
    try {
      const response = await axios.put(
        `/api/v1/super_athletes/${data.id}`,
        data
      );
      dispatch(slice.actions.putSuperAthleteSuccess(response.data));
    } catch (error) {
      openSnackbarWithError(error, dispatch);
      dispatch(slice.actions.putSuperAthleteFailure(error));
    }
  };
}

export function deleteSuperAthlete(superAthleteId, navigate) {
  dispatch(slice.actions.deleteSuperAthlete());
  return async (dispatch) => {
    try {
      await axios.delete(`/api/v1/super_athletes/${superAthleteId}`);
      dispatch(slice.actions.deleteSuperAthleteSuccess(superAthleteId));
      dispatch(
        openSnackbar({
          open: true,
          message: "Athlète supprimé de votre effectif.",
          variant: "alert",
          alert: {
            color: "success",
          },
          close: false,
        })
      );
      dispatch(decrementSuperAthleteCount());
      navigate("/dashboard/superathletes");
    } catch (error) {
      console.error(error);
      openSnackbarWithError(error, dispatch);
      dispatch(slice.actions.deleteSuperAthleteFailure(error));
    }
  };
}

export function closeAthleteModal() {
  return async (dispatch) => {
    dispatch(slice.actions.closeSuperAthlete(null));
  };
}

export function setIsAddingAthlete(value) {
  return async (dispatch) => {
    dispatch(slice.actions.isAddingAthlete(value));
  };
}
